import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient, Zeitplan, Modell, ProfilEintrag } from '../api';
import { IconZeitplan, IconPlus, IconLaeuft } from '../Icons';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState, Feld,
  FormSection, StatusBadge, type Spalte,
} from '../muster';

/** Was sich beauftragen laesst, in der Reihenfolge der Auftragsarten. */
const ARTEN = [
  { wert: 1,  text: 'IFC' },
  { wert: 2,  text: 'PDF' },
  { wert: 3,  text: 'DWG' },
  { wert: 10, text: 'Raumdaten lesen' },
  // 11 'Raumdaten schreiben' entfällt (WE1, Eigentümer 22.10.2026): Rückschreiben ist keine Regelaufgabe,
  // der Server weist es ab.
];

const WOCHENTAGE = [
  { wert: 1, text: 'Montag' },
  { wert: 2, text: 'Dienstag' },
  { wert: 3, text: 'Mittwoch' },
  { wert: 4, text: 'Donnerstag' },
  { wert: 5, text: 'Freitag' },
  { wert: 6, text: 'Samstag' },
  { wert: 7, text: 'Sonntag' },
];

/**
 * Wiederkehrende Exporte eines Projekts.
 *
 * Nachts laeuft die Verarbeitungsmaschine ohnehin - ein woechentlicher
 * IFC-Stand fuer die Koordination muss niemand von Hand anstossen.
 */
export function Zeitplaene({
  api, projektId, modelle, profile,
}: {
  api: ApiClient;
  projektId: string;
  modelle: Modell[];
  profile: ProfilEintrag[];
}) {
  const [plaene, setPlaene] = useState<Zeitplan[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [anlegen, setAnlegen] = useState(false);
  const [entfernen, setEntfernen] = useState<Zeitplan | null>(null);

  const laden = useCallback(() =>
    api.zeitplaene(projektId).then(setPlaene).catch(setFehler),
  [api, projektId]);

  useEffect(() => { laden(); }, [laden]);

  const versionen = profile.flatMap((p) =>
    p.versions.map((v) => ({ id: v.id, text: `${p.name} ${v.version}` })));

  const umschalten = useCallback(async (z: Zeitplan) => {
    await api.zeitplanAendern(z.id, {
      name: z.name,
      modelId: z.modelId,
      exportProfileVersionId: z.exportProfileVersionId,
      kinds: z.kinds.split(',').map(Number),
      rhythmus: z.rhythmus,
      wochentag: z.wochentag,
      monatstag: z.monatstag,
      uhrzeit: z.uhrzeit,
      benachrichtigung: z.benachrichtigung,
      isActive: !z.isActive,
    });
    laden();
  }, [api, laden]);

  const spalten: Spalte<Zeitplan>[] = useMemo(() => [
    {
      schluessel: 'name', titel: 'Name', immerSichtbar: true,
      wert: (z) => z.name,
      inhalt: (z) => <strong>{z.name}</strong>,
    },
    {
      schluessel: 'was', titel: 'Was',
      wert: (z) => artenText(z),
      inhalt: (z) => <span className="schwach">{artenText(z)}</span>,
    },
    {
      schluessel: 'wann', titel: 'Wann', breite: '170px',
      wert: (z) => wannText(z),
      inhalt: (z) => <span className="schwach">{wannText(z)}</span>,
    },
    {
      schluessel: 'modell', titel: 'Modell',
      wert: (z) => z.modell ?? '',
      inhalt: (z) => (z.modell
        ? <span className="schwach">{z.modell}</span>
        : <span className="sehr-schwach">alle Modelle</span>),
    },
    {
      /* Welche Fassung der NÄCHSTE Lauf nimmt — nicht die, die beim Anlegen
         gewählt wurde. Bis zum 16.09.2026 waren beide dasselbe, und genau
         deshalb liefen alle sieben Zeitpläne monatelang mit der
         unfreigegebenen Fassung 2.0.0. */
      schluessel: 'fassung', titel: 'Profilfassung', breite: '190px',
      wert: (z) => z.geltendeFassung ?? '',
      inhalt: (z) => (
        <span className="schwach">
          {z.geltendeFassung ?? <span className="sehr-schwach">—</span>}
          {z.profilfassungFest && (
            <span className="sehr-schwach" title="fest im Zeitplan hinterlegt">
              {' '}(fest)
            </span>
          )}
          {!z.profilfassungFest && z.hinterlegteFassung
            && z.hinterlegteFassung !== z.geltendeFassung && (
            <span
              className="sehr-schwach"
              title={`Im Zeitplan steht ${z.hinterlegteFassung}; `
                + 'gelaufen wird mit der freigegebenen Fassung.'}
            >
              {' '}(freigegeben)
            </span>
          )}
        </span>
      ),
    },
    {
      schluessel: 'naechster', titel: 'Nächster Lauf', art: 'datum', breite: '170px',
      wert: (z) => (z.isActive ? z.nextRunAt ?? '' : ''),
      inhalt: (z) => (z.isActive && z.nextRunAt
        ? (
          <span className="schwach">
            {new Date(z.nextRunAt).toLocaleString('de-DE', {
              weekday: 'short', day: '2-digit', month: '2-digit',
              hour: '2-digit', minute: '2-digit',
            })}
          </span>
        )
        : <StatusBadge zustand="neutral" text="stillgelegt" klein />),
    },
    {
      schluessel: 'zuletzt', titel: 'Zuletzt', art: 'status', breite: '150px',
      wert: (z) => (z.lastError ? 'Fehler' : z.lastJobNumber ?? ''),
      inhalt: (z) => (z.lastError
        ? <StatusBadge zustand="fehler" text="Fehler" klein titel={z.lastError} />
        : <span className="sehr-schwach">{z.lastJobNumber ?? '—'}</span>),
    },
    {
      schluessel: 'aktionen', titel: '', breite: '190px', sortierbar: false,
      ausrichtung: 'rechts',
      inhalt: (z) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button className="m-knopf"
                  onClick={(e) => { e.stopPropagation(); umschalten(z); }}>
            {z.isActive ? 'Stilllegen' : 'Aktivieren'}
          </button>{' '}
          <button className="m-knopf"
                  onClick={(e) => { e.stopPropagation(); setEntfernen(z); }}>
            Entfernen
          </button>
        </span>
      ),
    },
  ], [umschalten]);

  return (
    <ContentSection
      titel="Wiederkehrende Exporte"
      beschreibung={'Nachts läuft die Verarbeitungsmaschine ohnehin — ein '
        + 'wöchentlicher IFC-Stand für die Koordination muss niemand von Hand '
        + 'anstoßen.'}
      werkzeuge={<IconZeitplan />}
      aktionen={(
        <button className="m-knopf" onClick={() => setAnlegen(!anlegen)}
                disabled={versionen.length === 0}>
          {anlegen ? 'Abbrechen' : <><IconPlus size={16} />Zeitplan anlegen</>}
        </button>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Die Zeitpläne konnten nicht geladen werden."
                    technisch={fehler} beiErneutVersuchen={laden} />
      )}

      {versionen.length === 0 && (
        <p className="sehr-schwach">
          Erst ein Profil freigeben - ohne das gibt es nichts zu planen.
        </p>
      )}

      {anlegen && (
        <Formular
          api={api}
          projektId={projektId}
          modelle={modelle}
          versionen={versionen}
          fertig={() => { setAnlegen(false); laden(); }}
        />
      )}

      <DataTable
        spalten={spalten}
        zeilen={plaene ?? []}
        schluessel={(z) => z.id}
        laedt={plaene === null && fehler === null}
        ausfuhrDateiname="Zeitplaene"
        suche={(plaene ?? []).length > 8}
        leer={(
          <EmptyState
            icon={<IconZeitplan />}
            titel="Noch kein Zeitplan."
            erklaerung={'Ein wöchentlicher IFC-Stand für die Koordination etwa läuft am '
              + 'besten nachts — dann ist die Maschine frei und niemand wartet auf sein '
              + 'Ergebnis.'}
            aktion={versionen.length > 0 ? (
              <button className="m-knopf m-knopf-haupt" onClick={() => setAnlegen(true)}>
                <IconPlus size={16} />Zeitplan anlegen
              </button>
            ) : undefined}
          />
        )}
      />

      <ConfirmDialog
        offen={entfernen !== null}
        art="loeschen"
        titel={`Zeitplan „${entfernen?.name ?? ''}" entfernen?`}
        text="Bereits erzeugte Ergebnisse bleiben erhalten."
        beiBestaetigen={async () => {
          const z = entfernen;
          setEntfernen(null);
          if (!z) return;
          await api.zeitplanEntfernen(z.id);
          laden();
        }}
        beiAbbrechen={() => setEntfernen(null)}
      />
    </ContentSection>
  );
}

/** Die beauftragten Arten eines Zeitplans im Klartext. */
function artenText(z: Zeitplan): string {
  return z.kinds
    .split(',')
    .map((k) => ARTEN.find((a) => a.wert === Number(k))?.text ?? k)
    .join(', ');
}

/** Rhythmus und Uhrzeit in einem Satz. */
function wannText(z: Zeitplan): string {
  const uhrzeit = z.uhrzeit.slice(0, 5);

  if (z.rhythmus === 'taeglich') return `täglich ${uhrzeit}`;
  if (z.rhythmus === 'woechentlich') {
    return `${WOCHENTAGE.find((w) => w.wert === z.wochentag)?.text ?? '?'} ${uhrzeit}`;
  }
  return `am ${z.monatstag}. ${uhrzeit}`;
}

function Formular({
  api, projektId, modelle, versionen, fertig,
}: {
  api: ApiClient;
  projektId: string;
  modelle: Modell[];
  versionen: { id: string; text: string }[];
  fertig: () => void;
}) {
  const [name, setName] = useState('');
  const [arten, setArten] = useState<number[]>([1]);
  const [version, setVersion] = useState(versionen[0]?.id ?? '');
  const [modell, setModell] = useState('');
  const [rhythmus, setRhythmus] = useState('woechentlich');
  const [wochentag, setWochentag] = useState(1);
  const [monatstag, setMonatstag] = useState(1);
  const [uhrzeit, setUhrzeit] = useState('01:00');
  const [empfaenger, setEmpfaenger] = useState('');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.zeitplanAnlegen(projektId, {
        name: name.trim(),
        modelId: modell || null,
        exportProfileVersionId: version,
        kinds: arten,
        rhythmus,
        wochentag: rhythmus === 'woechentlich' ? wochentag : null,
        monatstag: rhythmus === 'monatlich' ? monatstag : null,
        uhrzeit: uhrzeit + ':00',
        benachrichtigung: empfaenger.trim() || null,
      });
      fertig();
    } catch (e) {
      setFehler(e);
      setLaeuft(false);
    }
  };

  return (
    <FormSection
      titel="Neuer Zeitplan"
      spalten={2}
      fuss={(
        <button className="m-knopf m-knopf-haupt" onClick={speichern}
                disabled={!name.trim() || arten.length === 0 || !version || laeuft}>
          {laeuft ? 'wird angelegt …' : 'Anlegen'}
        </button>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Der Zeitplan konnte nicht angelegt werden."
                    technisch={fehler} />
      )}

      <Feld
        beschriftung="Name"
        wert={name}
        beiAenderung={(w) => setName(w as string)}
        platzhalter="Wöchentlicher IFC-Stand"
        pflicht
      />

      <Feld
        art="mehrfachauswahl"
        beschriftung="Was"
        wert={arten.map(String)}
        beiAenderung={(w) => setArten((w as string[]).map(Number))}
        optionen={ARTEN.map((a) => ({
          wert: String(a.wert), beschriftung: a.text,
        }))}
        pflicht
      />

      <Feld
        art="auswahl"
        beschriftung="Profil"
        wert={version}
        beiAenderung={(w) => setVersion(w as string)}
        optionen={versionen.map((v) => ({ wert: v.id, beschriftung: v.text }))}
        pflicht
      />

      <Feld
        art="auswahl"
        beschriftung="Modell"
        wert={modell}
        beiAenderung={(w) => setModell(w as string)}
        hinweis="Ohne Auswahl wachsen neue Modelle automatisch mit hinein."
        optionen={[
          { wert: '', beschriftung: 'alle aktiven Modelle' },
          ...modelle.filter((m) => m.isActive)
            .map((m) => ({ wert: m.id, beschriftung: m.name })),
        ]}
      />

      <Feld
        art="auswahl"
        beschriftung="Rhythmus"
        wert={rhythmus}
        beiAenderung={(w) => setRhythmus(w as string)}
        optionen={[
          { wert: 'taeglich', beschriftung: 'täglich' },
          { wert: 'woechentlich', beschriftung: 'wöchentlich' },
          { wert: 'monatlich', beschriftung: 'monatlich' },
        ]}
      />

      {rhythmus === 'woechentlich' && (
        <Feld
          art="auswahl"
          beschriftung="Wochentag"
          wert={String(wochentag)}
          beiAenderung={(w) => setWochentag(Number(w))}
          optionen={WOCHENTAGE.map((t) => ({
            wert: String(t.wert), beschriftung: t.text,
          }))}
        />
      )}

      {rhythmus === 'monatlich' && (
        <Feld
          art="auswahl"
          beschriftung="Monatstag"
          wert={String(monatstag)}
          beiAenderung={(w) => setMonatstag(Number(w))}
          optionen={Array.from({ length: 28 }, (_, i) => i + 1).map((t) => ({
            wert: String(t), beschriftung: `${t}.`,
          }))}
        />
      )}

      {/* Die Uhrzeit bleibt ein `input type="time"`: Die Musterbibliothek
          kennt `datum`, aber keine Uhrzeit, und der Uhrzeitwähler des
          Browsers ist eine Funktion, die der Umzug nicht kosten darf. Die
          Beschriftung hängt wie bei `Feld` über `label for` daran. */}
      <div className="m-feld m-feld-halb">
        <label className="m-feld-beschriftung" htmlFor="zeitplan-uhrzeit">
          Uhrzeit
        </label>
        <input
          id="zeitplan-uhrzeit"
          type="time"
          className="m-feld-eingabe"
          value={uhrzeit}
          onChange={(e) => setUhrzeit(e.target.value)}
        />
        <span className="m-feld-hinweis">
          Ortszeit — sie bleibt auch bei der Zeitumstellung. Nachts ist die
          Verarbeitungsmaschine frei.
        </span>
      </div>

      <Feld
        beschriftung="Ergebnis an"
        wert={empfaenger}
        beiAenderung={(w) => setEmpfaenger(w as string)}
        platzhalter="niemand"
        hinweis={'Leer lassen, wenn niemand benachrichtigt werden soll — bei einem '
          + 'nächtlichen Lauf ist das meist richtig.'}
      />

      <p className="sehr-schwach" style={{ gridColumn: '1 / -1' }}>
        <IconLaeuft size={14} /> Ein großes Modell braucht zwanzig Minuten, und
        tagsüber wartet dann jemand auf sein Ergebnis.
      </p>

      {arten.includes(11) && (
        <div className="hinweis warnung" style={{ gridColumn: '1 / -1' }}>
          <span className="hinweis-zeichen">!</span>
          <div>
            <strong>Raumdaten schreiben verändert das Modell</strong> und gleicht es
            mit der Zentraldatei ab. Als wiederkehrender Auftrag geschieht das ohne
            Zutun - prüfen Sie, ob das gewollt ist.
          </div>
        </div>
      )}
    </FormSection>
  );
}
